import styled from "@emotion/styled";
import * as React from "react";

import { DesignToken, Text } from "@livechat/design-system-react-components";

import { ResponsiveBreakpoint, ResponsiveBreakpointType } from "constants/responsive-breakpoint";
import { isInIframe } from "helpers/app";
import { ViewHeaderInterface } from "interfaces/viewHeaderInterface";
import { withVar } from "themes/helpers";

import { viewHeaderTitleCss } from "./_styled";

export const ViewHeader: React.FC<ViewHeaderInterface> = props => {
  const { showBackButton, backButton, leftNodes, rightNodes } = props;
  const { title, subtitle, ...styledTitleProps } = props;

  const isIframe = isInIframe();

  const titleElement = isIframe ? (
    <StyledText {...styledTitleProps} subtitle={subtitle} size="md" bold>
      {title}
    </StyledText>
  ) : (
    <StyledTitle {...styledTitleProps} subtitle={subtitle} css={viewHeaderTitleCss}>
      {title}
    </StyledTitle>
  );

  return (
    <StyledHeader data-testid="page-header">
      {(showBackButton || leftNodes) && (
        <StyledLeftElements data-testid="header-back-button">
          {showBackButton && backButton}
          {leftNodes}
        </StyledLeftElements>
      )}
      {title && titleElement}
      {subtitle && <StyledSubtitle size="xs">{subtitle}</StyledSubtitle>}
      {rightNodes && <StyledRightElements>{rightNodes}</StyledRightElements>}
    </StyledHeader>
  );
};

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  height: 52px;
  position: relative;
  text-align: center;
  box-sizing: border-box;
  border-bottom: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};
  background-color: ${withVar(DesignToken.SurfacePrimaryDefault)};
`;

const StyledTitle = styled.div<Omit<ViewHeaderInterface, "title">>`
  height: 52px;
  line-height: 52px;
  padding-left: ${(props): string => (props.placement === "main-content" ? "0" : "16px")};
  position: absolute;
  box-sizing: border-box;
  margin: 0 40px 0 0;
  width: ${(props): string => (props.placement === "main-content" ? "100%" : "calc(100% - 40px);")};
  left: 0px;
  overflow: hidden;
  text-align: ${(props): string => (props.placement === "main-content" ? "center" : "left")};
  text-overflow: ellipsis;
  margin: 0 ${(props): number => (props.showBackButton && props.placement !== "main-content" ? 40 : 0)}px;
  ${({ subtitle }): string => (subtitle ? "top: -6px" : "")};
  @media (max-width: ${ResponsiveBreakpoint[ResponsiveBreakpointType.Mobile]}px) {
    text-align: center;
    padding-left: 0;
    margin: 0;
    width: 100%;
  }
`;

const StyledText = StyledTitle.withComponent(Text);

const StyledSubtitle = styled(Text)`
  position: absolute;
  width: 100%;
  left: 0;
  bottom: 3px;
  letter-spacing: 1.4px;
  color: ${withVar(DesignToken.ContentBasicSecondary)};
  @media (max-width: ${ResponsiveBreakpoint[ResponsiveBreakpointType.Mobile]}px) {
    text-align: center;
    padding-left: 0;
    margin: 0;
    width: 100%;
  }
`;

const StyledLeftElements = styled.div`
  position: absolute;
  left: 0;
  max-width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const StyledRightElements = styled.div`
  position: absolute;
  right: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
